import { motion } from "framer-motion";
import { ArrowRight, Quote, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FadeUp } from "./Motion";

// Key analytics proof — extracted from the 學員好評 compilation screenshot
const PROOF_STATS = [
  { value: "1,015,956", label: "單條 Reel 觀看次數", hint: "單篇破 101 萬" },
  { value: "1,028,302", label: "學員單條影片最高流量", hint: "單月紀錄" },
  { value: "283,046", label: "單月觸及帳戶數", hint: "+1,613% 爆增" },
  { value: "38 萬+", label: "實戰後一個月內 views", hint: "Io Hang L." },
];

// Chat-style testimonials — wrapped in iPhone mockups
const CHAT_TESTIMONIALS = [
  {
    id: "isaac",
    name: "Isaac C.",
    role: "Web3 × Invest KOL",
    src: "/community/processed/isaac-c.png",
    highlight: "影片不夠 24 小時已經有 10 萬 views",
    platform: "Messenger",
    tilt: -3,
  },
  {
    id: "ray",
    name: "Ray C.",
    role: "代運營操盤手",
    src: "/community/processed/ray-c.png",
    highlight: "多了百幾個 fans，而且都是真實帳號",
    platform: "WhatsApp",
    tilt: 2,
  },
  {
    id: "sarah",
    name: "Sarah L.",
    role: "旅遊 IP 博主",
    src: "/community/processed/sarah-whatsapp.png",
    highlight: "12 小時後 13.1 萬 view、5,451 likes",
    platform: "WhatsApp",
    tilt: -2,
  },
];

// Community post screenshots — wrapped as polaroids
const POLAROID_TESTIMONIALS = [
  {
    id: "mandy",
    name: "Mandy T.",
    industry: "新手媽媽育兒教室",
    src: "/community/processed/new-mandy.png",
    objectPosition: "top",
    quote: "兩個月 6,000 Followers 增長",
    tilt: -3,
  },
  {
    id: "io",
    name: "Io Hang L.",
    industry: "AI × Web3 × Invest",
    src: "/community/processed/new-io-hang.png",
    objectPosition: "top",
    quote: "一個月 38 萬 views，多條片破 10 萬",
    tilt: 2,
  },
  {
    id: "sonic",
    name: "Sonic M.",
    industry: "寵物知識創作者",
    src: "/community/processed/new-sonic.jpg",
    objectPosition: "top",
    quote: "未夠 24 小時，44,000 views",
    tilt: -2,
  },
  {
    id: "travel",
    name: "Jasmine W.",
    industry: "旅遊 IP・第一條人設片",
    src: "/community/processed/travel-ip.png",
    objectPosition: "top",
    quote: "依循 Jacky 教的故事法，真的引起共鳴",
    tilt: 3,
  },
];

/* ========== iPhone Mockup ========== */
const IPhoneMockup = ({ item, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40, rotate: item.tilt }}
      whileInView={{ opacity: 1, y: 0, rotate: item.tilt }}
      whileHover={{ rotate: 0, scale: 1.03, y: -6 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
      className="relative mx-auto"
      data-testid={`community-iphone-${item.id}`}
    >
      {/* Gold glow behind phone */}
      <div className="absolute inset-0 -z-10 blur-3xl opacity-30"
           style={{ background: "radial-gradient(circle at 50% 50%, rgba(212,180,106,0.4), transparent 70%)" }} />

      {/* Phone body */}
      <div className="relative w-[240px] sm:w-[260px] mx-auto rounded-[38px] p-[5px] bg-gradient-to-b from-[#1f1c16] via-[#14110c] to-[#1f1c16] shadow-[0_30px_80px_rgba(0,0,0,0.7),0_0_0_1px_rgba(212,180,106,0.25)]">
        {/* Screen */}
        <div className="relative rounded-[33px] overflow-hidden bg-white aspect-[9/19.5]">
          {/* Notch */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-[92px] h-[22px] bg-[#0a0907] rounded-full z-20" />
          {/* Screenshot */}
          <img
            src={item.src}
            alt={`${item.name} testimonial`}
            className="w-full h-full object-cover object-top"
            loading="lazy"
          />
          {/* Bottom platform badge */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/75 backdrop-blur-sm text-[10px] tracking-[0.22em] uppercase text-[hsl(var(--lime))] font-semibold z-20">
            {item.platform}
          </div>
        </div>
      </div>

      {/* Caption under phone */}
      <div className="mt-5 text-center max-w-[280px] mx-auto">
        <div className="flex items-center justify-center gap-2 mb-2">
          <span className="w-6 h-6 rounded-full bg-[hsl(var(--lime))] text-[#0a0907] font-serif-tc font-bold flex items-center justify-center text-[11px]">
            {item.name.charAt(0)}
          </span>
          <span className="font-serif-tc text-[15px] text-[hsl(var(--foreground))] font-semibold">{item.name}</span>
          <span className="text-[11px] text-[hsl(var(--muted-foreground))]">· {item.role}</span>
        </div>
        <p className="font-serif-tc text-[14px] leading-relaxed text-[hsl(var(--foreground))]/85 italic">
          「{item.highlight}」
        </p>
      </div>
    </motion.div>
  );
};

/* ========== Polaroid ========== */
const Polaroid = ({ item, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, rotate: item.tilt }}
      whileInView={{ opacity: 1, y: 0, rotate: item.tilt }}
      whileHover={{ rotate: 0, scale: 1.04, y: -8, zIndex: 10 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="relative"
      data-testid={`community-polaroid-${item.id}`}
    >
      <div className="bg-[#F5EFE0] rounded-[4px] p-2.5 pb-0 shadow-[0_20px_40px_rgba(0,0,0,0.5),0_0_0_1px_rgba(212,180,106,0.2)] border border-[rgba(212,180,106,0.15)] w-full">
        {/* Screenshot image */}
        <div
          className="relative overflow-hidden aspect-[3/4]"
          style={{ backgroundColor: item.bgColor || "#FFFFFF" }}
        >
          <img
            src={item.src}
            alt={`${item.name} community post`}
            className="w-full h-full"
            style={{
              objectFit: item.objectFit || "cover",
              objectPosition: item.objectPosition || "top",
            }}
            loading="lazy"
          />
          {/* Subtle gold top-left tape */}
          <div className="absolute -top-2 left-4 w-12 h-5 bg-[rgba(212,180,106,0.35)] rotate-[-8deg] blur-[0.5px] shadow-sm" />
        </div>

        {/* Polaroid caption */}
        <div className="py-4 px-1 text-center">
          <div className="font-serif-tc text-[15px] font-bold text-[#0a0907] leading-tight">{item.name}</div>
          <div className="text-[10.5px] text-[#5a564d] mt-0.5 uppercase tracking-wider">{item.industry}</div>
          <div className="mt-2 flex items-start gap-1 justify-center">
            <Quote className="w-3 h-3 text-[#B8923E] flex-shrink-0 mt-0.5" />
            <p className="font-serif-tc text-[12.5px] text-[#0a0907] leading-snug italic">{item.quote}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/* ========== Main Section ========== */
export const CommunityProof = ({ onCtaClick }) => {
  return (
    <section id="community-proof" className="relative py-16 sm:py-28 px-4 sm:px-6 bg-ink-warm overflow-hidden">
      {/* Ambient spotlight */}
      <div className="absolute inset-0 spotlight-xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto">
        {/* Eyebrow + Headline */}
        <FadeUp>
          <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-14">
            <div className="eyebrow justify-center mb-4"><span className="dot" /> PROOF · UNFILTERED</div>
            <h2 className="font-serif-tc text-[30px] sm:text-5xl lg:text-6xl font-bold leading-[1.15] tracking-tight">
              學員<span className="lime-keyword">實戰情況</span>
            </h2>
            <p className="mt-4 sm:mt-5 text-[14px] sm:text-[16px] text-[hsl(var(--muted-foreground))] leading-relaxed px-2">
              以下都是學員在社群、WhatsApp、Messenger 發出的<span className="text-[hsl(var(--foreground))]">原圖</span>。
            </p>
          </div>
        </FadeUp>

        {/* Analytics Stats Banner */}
        <FadeUp delay={0.05}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-14 sm:mb-20">
            {PROOF_STATS.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="relative p-4 sm:p-6 rounded-xl border border-[rgba(212,180,106,0.2)] bg-[rgba(212,180,106,0.04)] hover:border-[rgba(212,180,106,0.45)] hover:bg-[rgba(212,180,106,0.08)] transition-all duration-300 group"
                data-testid={`community-stat-${i}`}
              >
                <div className="flex items-center gap-1.5 mb-2">
                  <TrendingUp className="w-3 h-3 text-[hsl(var(--lime))]" />
                  <span className="text-[9.5px] sm:text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--lime))] font-bold">Real data</span>
                </div>
                <div className="stat-num text-[26px] sm:text-[38px] lg:text-[46px] leading-none lime-keyword">
                  {s.value}
                </div>
                <div className="mt-2 text-[11.5px] sm:text-[13px] text-[hsl(var(--foreground))]/90 font-semibold leading-snug">{s.label}</div>
                <div className="mt-0.5 text-[10px] sm:text-[11px] text-[hsl(var(--muted-foreground))]">{s.hint}</div>
              </motion.div>
            ))}
          </div>
        </FadeUp>

        {/* ===== iPhone Mockups - Chat screenshots ===== */}
        <FadeUp delay={0.1}>
          <div className="mb-6 sm:mb-10">
            <div className="flex items-center gap-2.5 mb-2">
              <div className="h-px w-8 bg-[rgba(212,180,106,0.5)]" />
              <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-semibold text-[hsl(var(--lime))]">
                01 / Direct Messages
              </span>
            </div>
            <h3 className="font-serif-tc text-[22px] sm:text-3xl text-[hsl(var(--foreground))] font-bold">
              會員實戰情況
            </h3>
          </div>
        </FadeUp>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 sm:gap-8 mb-20 sm:mb-28 px-4 sm:px-0">
          {CHAT_TESTIMONIALS.map((item, i) => (
            <IPhoneMockup key={item.id} item={item} index={i} />
          ))}
        </div>

        {/* ===== Polaroid Wall - Community posts ===== */}
        <FadeUp>
          <div className="mb-8 sm:mb-12">
            <div className="flex items-center gap-2.5 mb-2">
              <div className="h-px w-8 bg-[rgba(212,180,106,0.5)]" />
              <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-semibold text-[hsl(var(--lime))]">
                02 / Community Posts
              </span>
            </div>
            <h3 className="font-serif-tc text-[22px] sm:text-3xl text-[hsl(var(--foreground))] font-bold">
              社群每日實戰分享
            </h3>
          </div>
        </FadeUp>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          {POLAROID_TESTIMONIALS.map((item, i) => (
            <Polaroid key={item.id} item={item} index={i} />
          ))}
        </div>

        {/* Closing callout + CTA */}
        <FadeUp delay={0.1}>
          <div className="mt-16 sm:mt-24 text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[rgba(212,180,106,0.3)] bg-[rgba(212,180,106,0.06)] mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--lime))] animate-pulse" />
              <span className="text-[11px] sm:text-[12px] text-[hsl(var(--foreground))]/85 font-medium">
                這些截圖只是冰山一角・還有<span className="font-num font-bold text-[hsl(var(--lime))]"> 無數 </span>個案例每天都在社群發生
              </span>
            </div>
            <p className="font-serif-tc text-[16px] sm:text-xl text-[hsl(var(--foreground))]/90 leading-relaxed mb-6 sm:mb-8">
              他們能做到 — <span className="lime-keyword">你也可以</span>。<br />
              分享會現場拆解：他們是如何由 0 起步，用同一套系統複製結果。
            </p>
            <Button
              onClick={onCtaClick}
              className="btn-lime btn-lime-pulse h-12 sm:h-13 px-7 sm:px-8 text-[15px] sm:text-base"
              data-testid="community-proof-cta"
            >
              我要成為下一個案例主角 <ArrowRight className="ml-1.5 w-4 h-4" />
            </Button>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};
